import { FC, useEffect, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import qs from 'query-string';
import dayjs from 'dayjs';

import { IconArrowLeft, IconLike, IconLiked } from '../../icons';
import Page from '../../components/Page/Page';
import Loader from '../../components/Loader/Loader';
import { iNewsNavigationState } from '../News/News';
import RoutesEnum from '../../core/enums/RoutesEnum';
import { processHtmlLinksForNewTab } from '../../core/helpers';
import useNewsArticleDetails from '../../core/apiHooks/useNewsArticleDetails';
import useRecentNewsArticles from '../../core/apiHooks/useRecentNewsArticles';
import useNewsArticleFeedback from '../../core/apiHooks/useNewsArticleFeedback';

import './NewsArticle.styles.scss';
import NewsArticleReferences from './NewsArticleReferences/NewsArticleReferences';
import DownloadArticlePdfButton from './DownloadArticlePdfButton/DownloadArticlePdfButton';
import CopySubArticleLinkButton from './CopySubArticleLinkButton/CopySubArticleLinkButton';

const HIGHLIGHT_CLASS = 'NewsArticle__content-highlight';

const MAX_SCROLL_ATTEMPTS = 5;
const SCROLL_RETRY_DELAY = 100;

// Daily Dish sub-articles are stored as <p id="sub-{n}"> (backend contract, NewsSubArticleParser.IdPrefix)
const SUB_ARTICLE_SELECTOR = 'p[id^="sub-"]';
// The selector also matches editor ids such as `sub-heading`; only `sub-{numeric id}` is a real sub-article
const SUB_ARTICLE_ID = /^sub-\d+$/;
const SUBLINK_SLOT_CLASS = 'NewsArticle__sublink-slot';
const SUBLINK_NOWRAP_CLASS = 'NewsArticle__sublink-nowrap';

interface iSubLinkSlot {
    anchorId: string;
    slot: HTMLElement;
}

const isTitleRun = (el: Element) => el.tagName === 'B' || el.tagName === 'STRONG';
const hasVisibleText = (el: Element) => !!el.textContent?.trim();

/**
 * The sub-article's title: the first bold run with visible text, preferring direct children of the paragraph.
 * Empty runs are skipped like the backend's NewsSubArticleParser.ExtractTitle does (real samples start with `<b></b>`).
 */
const findTitleRun = (paragraph: Element): Element | undefined =>
    Array.from(paragraph.children).find((el) => isTitleRun(el) && hasVisibleText(el)) ||
    Array.from(paragraph.querySelectorAll('b, strong')).find(hasVisibleText);

/** The last text node under `root` that contains a non-whitespace character, walking from `lastChild` backwards */
const findLastWordTextNode = (root: Node): Text | null => {
    for (let node = root.lastChild; node; node = node.previousSibling) {
        if (node instanceof Text) {
            if (node.data.trim()) return node;
        } else {
            const found = findLastWordTextNode(node);
            if (found) return found;
        }
    }

    return null;
};

/**
 * Places `slot` inside the title run, wrapped together with the title's last word in a nowrap span, so the button
 * can't drop onto a line of its own (Chrome breaks before an inline-flex button even after a U+2060 word joiner).
 * Returns a function that restores the title text exactly, or null when the title has no word to attach to,
 * or when that word sits inside a link (the button must never be inside an <a>).
 */
const attachSlotToLastWord = (title: Element, slot: HTMLElement): (() => void) | null => {
    const textNode = findLastWordTextNode(title);
    if (!textNode) return null;

    const linkAncestor = textNode.parentElement?.closest('a');
    if (linkAncestor && title.contains(linkAncestor)) return null;

    const original = textNode.data;
    const trimmed = original.replace(/\s+$/, '');
    const lastWord = /\S+$/.exec(trimmed);
    if (!lastWord) return null;

    const wrapper = document.createElement('span');
    wrapper.className = SUBLINK_NOWRAP_CLASS;
    wrapper.append(lastWord[0], slot);

    // Trailing whitespace stays outside the nowrap wrapper so e.g. `<b>Title: </b>Body` keeps its space and break point
    const trailing = document.createTextNode(original.slice(trimmed.length));

    textNode.data = trimmed.slice(0, lastWord.index);
    textNode.after(wrapper, trailing);

    return () => {
        wrapper.remove();
        trailing.remove();
        textNode.data = original;
    };
};

const NewsArticle: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { state, pathname, search, hash } = useLocation();

    const returnUrl = state?.returnUrl || RoutesEnum.News;
    const articleId = Number.parseInt(id!, 10);

    const { data: article, isLoading, isSuccess } = useNewsArticleDetails(articleId);
    const { recentNewsArticles: featuredNewsArticles } = useRecentNewsArticles();
    const { isLiked, toggleLikeFeedback } = useNewsArticleFeedback(articleId);

    const contentRef = useRef<HTMLDivElement>(null);
    const [subLinkSlots, setSubLinkSlots] = useState<iSubLinkSlot[]>([]);

    // Memoized so re-renders don't re-run the link processing; React only resets the innerHTML (and drops the
    // injected slots) when the string's value changes, which here means the article content changed
    const contentHtml = useMemo(() => (article?.content ? processHtmlLinksForNewTab(article.content) : ''), [article?.content]);

    const handleBackToNews = useCallback(() => {
        navigate(returnUrl);
    }, [navigate, returnUrl]);

    useEffect(() => {
        if (isSuccess === false) {
            handleBackToNews();
            return;
        }

        if (!isLoading && (!article?.content || !article?.id)) {
            handleBackToNews();
            return;
        }

        if (!isLoading && isSuccess === true) {
            const existingParams = search ? qs.parse(search) : {};

            if (existingParams.loaded === 'true') return;

            const timer = setTimeout(() => {
                const newParams = { ...existingParams, loaded: 'true' };
                const newSearchString = qs.stringify(newParams);
                navigate(`${pathname}?${newSearchString}${hash}`, { replace: true });
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [article?.content, article?.id, handleBackToNews, navigate, isLoading, isSuccess, pathname, search, hash]);

    useEffect(() => {
        if (isLoading || !article?.content || !hash) return;

        const targetId = hash.slice(1); // drop the leading '#'
        if (!targetId) return;

        let attempts = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let highlighted: HTMLElement | null = null;

        const tryScroll = () => {
            const target = document.getElementById(targetId);

            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                target.classList.add(HIGHLIGHT_CLASS);
                highlighted = target;
                return;
            }

            if (attempts++ < MAX_SCROLL_ATTEMPTS) {
                timer = setTimeout(tryScroll, SCROLL_RETRY_DELAY);
            }
        };

        tryScroll();

        return () => {
            clearTimeout(timer);
            highlighted?.classList.remove(HIGHLIGHT_CLASS);
        };
    }, [isLoading, article?.content, hash]);

    // Insert an empty slot at the end of each sub-article's bold title; the copy-link buttons are portaled into them.
    // The stored HTML stays untouched (it also feeds PDFs and emails) and the <p id> nodes are never replaced.
    useLayoutEffect(() => {
        const container = contentRef.current;

        if (!container) {
            setSubLinkSlots([]);
            return;
        }

        const created: iSubLinkSlot[] = [];
        const restores: (() => void)[] = [];

        container.querySelectorAll<HTMLElement>(SUB_ARTICLE_SELECTOR).forEach((paragraph) => {
            if (!SUB_ARTICLE_ID.test(paragraph.id) || paragraph.querySelector(`.${SUBLINK_SLOT_CLASS}`)) return;

            const title = findTitleRun(paragraph);
            if (!title) return;

            const slot = document.createElement('span');
            slot.className = SUBLINK_SLOT_CLASS;

            // Never place the button inside a link: when the title is wrapped in one, the slot goes after the <a>
            const closestLink = title.closest('a');
            const outerLink = closestLink && paragraph.contains(closestLink) ? closestLink : null;
            const restore = outerLink ? null : attachSlotToLastWord(title, slot);

            if (restore) {
                restores.push(restore);
            } else {
                (outerLink || title).after(slot);
                restores.push(() => slot.remove());
            }

            created.push({ anchorId: paragraph.id, slot });
        });

        setSubLinkSlots(created);

        // Undo exactly what this run did, so StrictMode's cleanup + re-run never splits more words or loses text
        return () => {
            restores.forEach((restore) => restore());
        };
    }, [contentHtml, isLoading]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Page title={article?.title || 'News Article'} isResponsive>
            <div className="NewsArticle">
                <div className="NewsArticle__header">
                    <div className="NewsArticle__breadcrumb" onClick={handleBackToNews}>
                        <IconArrowLeft className="Icon16" />
                        <Link to={returnUrl} className="NewsArticle__breadcrumb-text">
                            Back to News Archive
                        </Link>
                    </div>

                    {article?.featuredImage && (
                        <div className="NewsArticle__image">
                            <img src={article.featuredImage} alt={article.title || 'Article featured'} />
                        </div>
                    )}
                </div>

                <div className="NewsArticle__meta">
                    <div className="NewsArticle__title">{article?.title || 'headline goes here'}</div>

                    {article?.publishedDate && (
                        <div className="NewsArticle__info">
                            <span>{article?.publishedDate}</span>
                        </div>
                    )}

                    <div className="NewsArticle__content">
                        {isLoading && (
                            <div className="NewsArticle__content-loading">
                                <Loader />
                            </div>
                        )}

                        {!isLoading && article?.content && article.content.length > 0 && (
                            <div
                                ref={contentRef}
                                className="NewsArticle__content-html"
                                dangerouslySetInnerHTML={{
                                    __html: contentHtml
                                }}
                            />
                        )}

                        {subLinkSlots.map(({ anchorId, slot }) =>
                            createPortal(<CopySubArticleLinkButton articleId={articleId} anchorId={anchorId} />, slot, anchorId)
                        )}
                    </div>

                    <div className="NewsArticle__footer">
                        <div className="NewsArticle__tags">
                            {!isLoading && article?.tagItems && article.tagItems.length > 0 && (
                                <>
                                    <div className="NewsArticle__tags-label">news tags</div>
                                    <div className="NewsArticle__tags-list">
                                        {article.tagItems.map((tag) => (
                                            <Link
                                                key={tag.id}
                                                to={`${RoutesEnum.News}?orderBy=publishedDate&sortOrder=desc&tagsIds=${tag.id}`}
                                                className="NewsArticle__tags-item"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {tag.value}
                                            </Link>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="NewsArticle__feedback">
                            <div
                                className={'NewsArticle__feedback-icon'}
                                data-testid="buttonLikeArticle"
                                onClick={() => toggleLikeFeedback(articleId, isLiked === true ? null : true)}
                            >
                                {isLiked === true ? <IconLiked className="Icon32" /> : <IconLike className="Icon32" />}
                            </div>
                            <div
                                className="NewsArticle__feedback-icon is-dislike-icon"
                                data-testid="buttonDislikeArticle"
                                onClick={() => toggleLikeFeedback(articleId, isLiked === false ? null : false)}
                            >
                                {isLiked === false ? <IconLiked className="Icon32" /> : <IconLike className="Icon32" />}
                            </div>
                            <DownloadArticlePdfButton
                                articleId={articleId}
                                articleTitle={article?.title}
                                className="NewsArticle__feedback-icon"
                            />
                        </div>
                    </div>

                    <div className="NewsArticle__recent">
                        <div className="NewsArticle__recent-wrapper">
                            <div className="NewsArticle__recent-container">
                                <div className="NewsArticle__recent-header">
                                    <h3 className="NewsArticle__recent-heading">most recent articles</h3>
                                </div>

                                <div className="NewsArticle__recent-content">
                                    <div className="NewsArticle__recent-articles">
                                        {featuredNewsArticles.slice(0, 3).map((recentArticle) => (
                                            <Link
                                                key={recentArticle.id}
                                                to={RoutesEnum.NewsArticle.replace(':id', String(recentArticle.id))}
                                                className="NewsArticle__recent-article"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <div className="NewsArticle__recent-article-title">{recentArticle.title}</div>
                                                {recentArticle.publishedDate && dayjs(recentArticle.publishedDate).isValid() && (
                                                    <div className="NewsArticle__recent-article-date">
                                                        {dayjs(recentArticle.publishedDate).format('MMMM D, YYYY')}
                                                    </div>
                                                )}
                                            </Link>
                                        ))}
                                    </div>

                                    <div className="NewsArticle__recent-archive">
                                        <Link to={returnUrl} className="NewsArticle__recent-archive-link">
                                            news archive
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <NewsArticleReferences articleId={articleId} />
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default NewsArticle;
